import React from 'react';
import styled from 'styled-components';

const CartItemContainer = styled.div`
  display: flex;
  padding: 1.5rem 0;
  border-bottom: 1px solid #eee;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ItemImage = styled.div`
  width: 100px;
  height: 100px;
  margin-right: 1.5rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
`;

const ItemPrice = styled.div`
  color: #555;
  margin-bottom: 0.5rem;
`;

const StockWarning = styled.div`
  color: #d93025;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  
  @media (max-width: 768px) {
    margin-top: 1rem;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1.5rem;
`;

const QuantityButton = styled.button`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  color: #333;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  
  &:hover {
    background-color: #e5e5e5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 40px;
  height: 30px;
  border: 1px solid #ddd;
  border-left: none;
  border-right: none;
  text-align: center;
  font-size: 0.9rem;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #d93025;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    margin-right: 0.25rem;
  }
`;

const ItemTotal = styled.div`
  margin-left: auto;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
  }
`;

// Trash icon SVG component
const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#d93025"/>
  </svg>
);

const CartItem = ({ item, onRemove, onUpdateQuantity, formatCurrency }) => {
  const maxStock = item.stock || 0;
  const isAtMaxStock = item.quantity >= maxStock;
  
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= maxStock) {
      onUpdateQuantity(value);
    }
  };
  
  const decrementQuantity = () => {
    if (item.quantity > 1) {
      onUpdateQuantity(item.quantity - 1);
    }
  };
  
  const incrementQuantity = () => {
    if (!isAtMaxStock) {
      onUpdateQuantity(item.quantity + 1);
    }
  };
  
  return (
    <CartItemContainer>
      <ItemImage>
        <img src={item.imageUrl || 'https://via.placeholder.com/100'} alt={item.name} />
      </ItemImage>
      
      <ItemDetails>
        <ItemName>{item.name}</ItemName>
        <ItemPrice>{formatCurrency(item.price)} each</ItemPrice>
        {isAtMaxStock && <StockWarning>Maximum stock reached ({maxStock} available)</StockWarning>}
        
        <ItemActions>
          <QuantityControl>
            <QuantityButton onClick={decrementQuantity} disabled={item.quantity <= 1}>
              -
            </QuantityButton>
            <QuantityInput
              type="number"
              min="1"
              max={maxStock}
              value={item.quantity}
              onChange={handleQuantityChange}
            />
            <QuantityButton onClick={incrementQuantity} disabled={isAtMaxStock}>
              +
            </QuantityButton>
          </QuantityControl>
          
          <RemoveButton onClick={onRemove}>
            <TrashIcon /> Remove
          </RemoveButton>
        </ItemActions>
      </ItemDetails>
      
      <ItemTotal>
        {formatCurrency(item.price * item.quantity)}
      </ItemTotal>
    </CartItemContainer>
  );
};

export default CartItem;
